import React, { useState, useEffect, useCallback } from "react";
import { Card, Collapse, Button } from "react-bootstrap";
import { BsChevronDown, BsChevronUp, BsGripVertical } from "react-icons/bs";
import { useDashMedidasSectionDragHandle } from "../../context/DashMedidasSectionOrderContext";
import "./DashMedidasSectionCard.css";

const STORAGE_PREFIX = "dashMedidasSectionCollapsed_";

const readCollapsedState = (sectionId, defaultCollapsed) => {
  if (!sectionId) return defaultCollapsed;
  try {
    const stored = sessionStorage.getItem(`${STORAGE_PREFIX}${sectionId}`);
    if (stored === null) return defaultCollapsed;
    return stored === "true";
  } catch {
    return defaultCollapsed;
  }
};

export const DashMedidasSectionHeader = ({
  icon: Icon,
  subtitle,
  title,
  headerRight,
  compact = false,
  collapsible = false,
  isOpen = true,
  onToggle,
  sectionId,
  dragHandleProps = null,
}) => (
  <div
    className={[
      "dash-section-card__header",
      compact ? "dash-section-card__header--compact" : "",
      collapsible && !isOpen ? "dash-section-card__header--collapsed" : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <div className="dash-section-card__header-left">
      {dragHandleProps && (
        <span {...dragHandleProps}>
          <BsGripVertical size={18} aria-hidden />
        </span>
      )}
      {Icon && (
        <div className="dash-section-card__icon" aria-hidden>
          <Icon />
        </div>
      )}
      <div className="dash-section-card__heading">
        {subtitle && (
          <small className="dash-section-card__subtitle">{subtitle}</small>
        )}
        {title && <h5 className="dash-section-card__title">{title}</h5>}
      </div>
    </div>

    <div className="dash-section-card__header-actions">
      {headerRight && (
        <div
          className="dash-section-card__header-right"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {typeof headerRight === "string" ? (
            <span className="dash-section-card__header-meta">{headerRight}</span>
          ) : (
            headerRight
          )}
        </div>
      )}

      {collapsible && (
        <Button
          type="button"
          variant="link"
          className="dash-section-card__collapse-btn"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={sectionId ? `collapse-${sectionId}` : undefined}
          aria-label={isOpen ? "Recolher seção" : "Expandir seção"}
        >
          {isOpen ? <BsChevronUp size={20} /> : <BsChevronDown size={20} />}
        </Button>
      )}
    </div>
  </div>
);

const DashMedidasSectionCard = ({
  icon,
  subtitle,
  title,
  headerRight,
  children,
  bodyClassName = "",
  className = "",
  compact = false,
  asPanel = false,
  panelClassName = "",
  collapsible = false,
  sectionId,
  defaultCollapsed = false,
  mountOnEnter = false,
  onExpand,
}) => {
  const isCollapsible = collapsible && sectionId && !compact && !asPanel;
  const dragHandleProps = useDashMedidasSectionDragHandle(sectionId);

  const [collapsed, setCollapsed] = useState(() =>
    readCollapsedState(sectionId, defaultCollapsed)
  );

  const isOpen = !collapsed;

  useEffect(() => {
    if (!sectionId) return;
    try {
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${sectionId}`,
        String(collapsed)
      );
    } catch {
      /* ignore */
    }
  }, [collapsed, sectionId]);

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const headerProps = {
    icon,
    subtitle,
    title,
    headerRight,
    collapsible: isCollapsible,
    isOpen,
    onToggle: handleToggle,
    sectionId,
    dragHandleProps,
  };

  if (asPanel) {
    return (
      <div className={`dash-section-card__panel ${panelClassName}`}>
        <DashMedidasSectionHeader {...headerProps} compact />
        <div className={`dash-section-card__panel-body ${bodyClassName}`}>
          {children}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <Card className={`dash-section-card dash-section-card--inner h-100 ${className}`}>
        <DashMedidasSectionHeader {...headerProps} compact />
        <Card.Body
          className={`dash-section-card__body dash-section-card__body--inner ${bodyClassName}`}
        >
          {children}
        </Card.Body>
      </Card>
    );
  }

  const bodyContent = (
    <Card.Body className={`dash-section-card__body ${bodyClassName}`}>
      {children}
    </Card.Body>
  );

  return (
    <Card
      className={[
        "dash-section-card",
        "shadow-sm",
        "border-0",
        isCollapsible && !isOpen ? "" : "h-100",
        isCollapsible && !isOpen ? "dash-section-card--folded" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <DashMedidasSectionHeader {...headerProps} />
      {isCollapsible ? (
        <Collapse in={isOpen} mountOnEnter={mountOnEnter} onEntered={onExpand}>
          <div id={`collapse-${sectionId}`}>{bodyContent}</div>
        </Collapse>
      ) : (
        bodyContent
      )}
    </Card>
  );
};

export default DashMedidasSectionCard;
